/**
 * Zones that stopped changing clocks on November 1, 2026 (tzdata 2026e), with
 * their fixed UTC offset in minutes from then on.
 */
export const CANADA_2026_FIXED_OFFSET_MINUTES: Readonly<
  Record<string, number>
> = {
  "America/Vancouver": -420,
  "Canada/Pacific": -420,
  "America/Edmonton": -360,
  "Canada/Mountain": -360,
  "America/Yellowknife": -360,
  "America/Inuvik": -360,
  "America/Winnipeg": -300,
  "Canada/Central": -300,
  "America/Rainy_River": -300,
};

// A winter day: old tz data puts these zones on standard time, new data on the
// fixed offset.
const PROBE_INSTANT = Date.UTC(2026, 11, 15, 12);

const staleByZone = new Map<string, boolean>();

/**
 * True when `offsetMinutes`, the offset this runtime reports for `timeZone`
 * on December 15, 2026, is not the fixed offset under the new rules.
 */
export function isStaleOffset(timeZone: string, offsetMinutes: number) {
  const fixed = CANADA_2026_FIXED_OFFSET_MINUTES[timeZone];
  return fixed !== undefined && offsetMinutes !== fixed;
}

/**
 * True when this runtime's Intl time zone data still has the old rules for
 * `timeZone`, so times from November 2026 on come out one hour off. Old
 * browsers are the usual cause; Node with tzdata 2026e returns false.
 */
export function hasStaleTimeZoneData(timeZone: string): boolean {
  if (!(timeZone in CANADA_2026_FIXED_OFFSET_MINUTES)) {
    return false;
  }
  let stale = staleByZone.get(timeZone);
  if (stale === undefined) {
    try {
      stale = isStaleOffset(
        timeZone,
        intlOffsetMinutes(PROBE_INSTANT, timeZone),
      );
    } catch {
      stale = false;
    }
    staleByZone.set(timeZone, stale);
  }
  return stale;
}

function intlOffsetMinutes(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(p => p.type === type)?.value);
  const wallAsUtc = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
  );
  return Math.round((wallAsUtc - instant) / 60_000);
}
